import {
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  Icon,
  IconButton,
  Skeleton,
  SkeletonText,
  Spacer,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';
import download from 'downloadjs';
import { Heading, Table } from '~/components';
import { useQueryParams } from '~/hooks';
import { RecentActivity } from '~/pages/Portal/Sinisters/RecentActivity';
import {
  useSinister,
  useSinisterDocuments,
} from '~/hooks/useSinisters/useSinisters';
import {
  getSinisterDocument,
  getSinisterDocumentsZip,
  getSinisterPdf,
} from '~/services/Sinisters';
import { SinisterDocuments, SinistersDocuments } from '~/types/sinisters';
import { STORAGE } from '~/constants';
import { SinistersMenuOptions } from '~/pages/Portal/Sinisters/SinistersMenuOptions';
import { SinisterAddDocumentForm } from '~/pages/Portal/Sinisters/SinisterAddDocumentForm';

export const SinisterDetail: React.VFC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const history = useHistory();
  const { data, isLoading } = useSinister(Number(id));
  const { data: documents, isLoading: isLoadingDocuments } =
    useSinisterDocuments(Number(id));
  const [isDownloading, setIsDownloading] = useState(false);
  const user = JSON.parse(localStorage.getItem(`${STORAGE}:user`) || '');

  const [checkedItems, setCheckedItems] = useState<number[]>([]);

  const query = useQueryParams();

  const handleZipDownload = () => {
    const idDocuments = documents
      ?.filter(d => {
        return checkedItems.includes(d.id_check);
      })
      .map(d => Number(d.id));

    const typesDocuments = documents
      ?.filter(d => {
        return checkedItems?.includes(d.id_check);
      })
      .map(d => d.tipo);

    if (idDocuments?.length && typesDocuments?.length) {
      getSinisterDocumentsZip(id, idDocuments, typesDocuments).then(res => {
        if (res?.data?.size > 0) {
          download(
            new Blob([res?.data], { type: res.data.type }),
            `documentos_sinistro_${data?.numero_sinistro}.zip`,
          );
        } else {
          toast({
            title: 'Erro ao fazer download!',
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      });
    }
  };

  function handleDownload(tipo: number) {
    setIsDownloading(true);
    const url = SinistersDocuments[tipo];
    getSinisterPdf(id, url)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: 'Documento não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }

  function handleDownloadDocument(documento: SinisterDocuments) {
    setIsDownloading(true);
    getSinisterDocument(id, documento.id, documento.tipo)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            `${documento.nome}`,
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        toast({
          title: 'Documento não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  }

  if (isLoading)
    return (
      <Box px="8" py="4">
        <Skeleton width="30%" height="20px" />
        <Flex
          flexDir="row"
          flexWrap="wrap"
          w="100%"
          py={9}
          gridGap="12"
          style={{
            position: 'relative',
          }}
        >
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
          <Center
            position="relative"
            bottom="0"
            display={{ md: 'block', sm: 'none' }}
          />
          <Stack maxW={400} w="100%" gridGap={4}>
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="1000px">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
        <Flex flexFlow="column" maxW="700px" mt="8">
          <Stack>
            <Skeleton width="200px" height="20px" />
            <SkeletonText mt="4" noOfLines={8} spacing="4" />
          </Stack>
        </Flex>
      </Box>
    );

  return (
    <Box px="8" py="4" paddingBottom="8rem">
      <Heading hasBackButton>Sinistro N° {data?.numero_sinistro}</Heading>
      <Flex
        flexDir="row"
        flexWrap="wrap"
        w="100%"
        py={9}
        gridGap="12"
        style={{
          position: 'relative',
        }}
        justify="space-between"
        maxW={1100}
        paddingBottom={0}
      >
        <Heading fontSize="md">Informações Gerais</Heading>
        {data && <SinistersMenuOptions sinistro={data} isDetail />}
      </Flex>
      <Flex
        flexDir="row"
        flexWrap="wrap"
        w="100%"
        py={9}
        gridGap="12"
        style={{
          position: 'relative',
        }}
      >
        <Stack maxW={500} w="100%" gridGap={4}>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Série:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.numero_serie}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              SKU:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.equipamento.sku}
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Modelo:
            </Text>
            <Text textAlign="right">
              <Text color="gray.500" fontSize="14px">
                {data?.equipamento.tipo_equipamento}
              </Text>
              <Text color="gray.500" fontSize="13px">
                {data?.equipamento.modelo}
              </Text>
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Cliente:
            </Text>
            <Text textAlign="right">
              <Text color="gray.500" fontSize="14px">
                {data?.empresa.cliente}
              </Text>
              <Text color="gray.500" fontSize="13px">
                {data?.empresa.cnpj}
              </Text>
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Grupo:
            </Text>
            <Text color="gray.500" fontSize="14px">
              {data?.empresa.grupo}
            </Text>
          </Flex>
          <Flex justify="space-between" align="baseline">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Responsável:
            </Text>
            <Text textAlign="right">
              <Text color="gray.500" fontSize="14px" textAlign="right">
                {data?.comunicado_por}
              </Text>
              <Text color="gray.500" fontSize="13px" textAlign="right">
                {data?.email}
              </Text>
            </Text>
          </Flex>
          <Flex justify="space-between" align="baseline">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Data:
            </Text>
            <Text color="gray.500" fontSize="14px" textAlign="right">
              {data?.formattedDate}
            </Text>
          </Flex>
          <Flex justify="space-between" align="baseline">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Tipo:
            </Text>
            <Text color="gray.500" fontSize="14px" textAlign="right">
              {data?.tipo.nome}
            </Text>
          </Flex>
          <Flex justify="space-between" align="baseline">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Declaração:
            </Text>
            <Text color="gray.500" fontSize="14px" textAlign="right">
              <IconButton
                colorScheme="gray"
                aria-label="Download"
                icon={<Icon as={FiDownload} />}
                onClick={() => handleDownload(0)}
                isDisabled={isDownloading || !data?.declaracao}
              />
            </Text>
          </Flex>
        </Stack>
        <Center
          position="relative"
          bottom="0"
          display={{ md: 'block', sm: 'none' }}
        >
          <Divider orientation="vertical" borderColor="gray.300" />
        </Center>
        <Stack maxW={500} w="100%" gridGap={4}>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Status:
            </Text>
            <Badge
              colorScheme={data?.colorScheme}
              px={2}
              py={1}
              borderRadius={4}
              w="150px"
              textAlign="center"
              fontSize="10px"
            >
              {data?.formattedStatus}
            </Badge>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Boletim de ocorrência:
            </Text>
            <Text color="gray.500" fontSize="14px">
              <IconButton
                colorScheme="gray"
                aria-label="Download"
                icon={<Icon as={FiDownload} />}
                onClick={() => handleDownload(2)}
                isDisabled={isDownloading || !data?.boletim_ocorrencia}
              />
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Outro:
            </Text>
            <Text color="gray.500" fontSize="14px">
              <IconButton
                colorScheme="gray"
                aria-label="Download"
                icon={<Icon as={FiDownload} />}
                onClick={() => handleDownload(3)}
                isDisabled={isDownloading || !data?.outro}
              />
            </Text>
          </Flex>
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Seguro Próprio:
            </Text>
            <Text color="gray.500" fontSize="14px" textAlign="right">
              {data?.apolice_propria ? 'SIM' : 'NÃO'}
            </Text>
          </Flex>
          {data?.apolice_propria && (
            <Flex justify="space-between" align="center">
              <Text fontWeight="medium" color="gray.800" fontSize="14px">
                Apólice de seguro:
              </Text>
              <Text color="gray.500" fontSize="14px">
                <IconButton
                  colorScheme="gray"
                  aria-label="Download"
                  icon={<Icon as={FiDownload} />}
                  onClick={() => handleDownload(1)}
                  isDisabled={isDownloading}
                />
              </Text>
            </Flex>
          )}
          {data?.numero_apolice && (
            <Flex justify="space-between" align="center">
              <Text fontWeight="medium" color="gray.800" fontSize="14px">
                N° apólice:
              </Text>
              <Text color="gray.500" fontSize="14px">
                {data?.numero_apolice}
              </Text>
            </Flex>
          )}
          {data?.seguradora && (
            <Flex justify="space-between" align="center">
              <Text fontWeight="medium" color="gray.800" fontSize="14px">
                Seguradora:
              </Text>
              <Text color="gray.500" fontSize="14px">
                {data?.seguradora.nome}
              </Text>
            </Flex>
          )}
          {data?.existe_carta_positivo && (
            <Flex justify="space-between" align="center">
              <Text fontWeight="medium" color="gray.800" fontSize="14px">
                Carta positivo:
              </Text>
              <Text color="gray.500" fontSize="14px">
                <IconButton
                  colorScheme="gray"
                  aria-label="Download"
                  icon={<Icon as={FiDownload} />}
                  onClick={() => handleDownload(4)}
                  isDisabled={isDownloading}
                />
              </Text>
            </Flex>
          )}
          {data?.existe_nota_de_remessa && (
            <Flex justify="space-between" align="center">
              <Text fontWeight="medium" color="gray.800" fontSize="14px">
                Nota de remessa:
              </Text>
              <Text color="gray.500" fontSize="14px">
                <IconButton
                  colorScheme="gray"
                  aria-label="Download"
                  icon={<Icon as={FiDownload} />}
                  onClick={() => handleDownload(5)}
                  isDisabled={isDownloading}
                />
              </Text>
            </Flex>
          )}
          {data?.existe_orcamento_de_reparo && (
            <Flex justify="space-between" align="center">
              <Text fontWeight="medium" color="gray.800" fontSize="14px">
                Orçamento de reparo:
              </Text>
              <Text color="gray.500" fontSize="14px">
                <IconButton
                  colorScheme="gray"
                  aria-label="Download"
                  icon={<Icon as={FiDownload} />}
                  onClick={() => handleDownload(6)}
                  isDisabled={isDownloading}
                />
              </Text>
            </Flex>
          )}
          <Flex justify="space-between" align="center">
            <Text fontWeight="medium" color="gray.800" fontSize="14px">
              Descrição:
            </Text>
            <Text color="gray.500" fontSize="14px" width="80%">
              {data?.descricao}
            </Text>
          </Flex>
        </Stack>
      </Flex>
      <Flex flexFlow="column" maxW="1080px" mt="30px">
        <Tabs variant="enclosed">
          <TabList>
            <Tab>Seguro</Tab>
            <Tab>Nota de débito</Tab>
            <Tab>Documentos</Tab>
          </TabList>

          <TabPanels>
            <TabPanel width="50%">
              <Flex justify="space-between" align="center" padding="1rem">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  N° apólice:
                </Text>
                <Text color="gray.500" fontSize="14px">
                  {data?.numero_apolice ? data?.numero_apolice : '-'}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center" padding="1rem">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Seguradora:
                </Text>
                <Text color="gray.500" fontSize="14px">
                  {data?.seguradora ? data?.seguradora?.nome : '-'}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center" padding="1rem">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Carta positivo:
                </Text>
                {data?.existe_carta_positivo ? (
                  <Text color="gray.500" fontSize="14px">
                    <IconButton
                      colorScheme="gray"
                      aria-label="Download"
                      icon={<Icon as={FiDownload} />}
                      onClick={() => handleDownload(4)}
                      isDisabled={isDownloading}
                    />
                  </Text>
                ) : (
                  <Text color="gray.500" fontSize="14px">
                    -
                  </Text>
                )}
              </Flex>
              <Flex justify="space-between" align="center" padding="1rem">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Nota de remessa:
                </Text>
                {data?.existe_nota_de_remessa ? (
                  <Text color="gray.500" fontSize="14px">
                    <IconButton
                      colorScheme="gray"
                      aria-label="Download"
                      icon={<Icon as={FiDownload} />}
                      onClick={() => handleDownload(5)}
                      isDisabled={isDownloading}
                    />
                  </Text>
                ) : (
                  <Text color="gray.500" fontSize="14px">
                    -
                  </Text>
                )}
              </Flex>
            </TabPanel>
            <TabPanel width="50%">
              <Flex justify="space-between" align="center" padding="1rem">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Valor:
                </Text>
                <Text color="gray.500" fontSize="14px">
                  {data?.nota_de_debito_valor
                    ? data?.formattedValorNotaDebito
                    : '-'}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center" padding="1rem">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Nota de débito:
                </Text>
                {data?.existe_nota_de_debito ? (
                  <Text color="gray.500" fontSize="14px">
                    <IconButton
                      colorScheme="gray"
                      aria-label="Download"
                      icon={<Icon as={FiDownload} />}
                      onClick={() => handleDownload(8)}
                      isDisabled={isDownloading}
                    />
                  </Text>
                ) : (
                  <Text color="gray.500" fontSize="14px">
                    -
                  </Text>
                )}
              </Flex>
              <Flex justify="space-between" align="center" padding="1rem">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Comprovante:
                </Text>
                {data?.existe_comprovante ? (
                  <Text color="gray.500" fontSize="14px">
                    <IconButton
                      colorScheme="gray"
                      aria-label="Download"
                      icon={<Icon as={FiDownload} />}
                      onClick={() => handleDownload(7)}
                      isDisabled={isDownloading}
                    />
                  </Text>
                ) : (
                  <Text color="gray.500" fontSize="14px">
                    -
                  </Text>
                )}
              </Flex>
              <Flex justify="space-between" align="center" padding="1rem">
                <Text fontWeight="medium" color="gray.800" fontSize="14px">
                  Descrição:
                </Text>
                <Text color="gray.500" fontSize="14px" maxWidth="80%">
                  {data?.nota_de_debito_referente
                    ? data?.nota_de_debito_referente
                    : '-'}
                </Text>
              </Flex>
            </TabPanel>
            <TabPanel>
              <Flex flexFlow="column" maxW="1080px">
                <Flex align="center" paddingBottom="1rem">
                  <Button
                    justify="start"
                    align="center"
                    color="white"
                    background="gray.900"
                    isDisabled={!(checkedItems.length > 1)}
                    _hover={{
                      bgColor: 'black',
                    }}
                    onClick={() => handleZipDownload()}
                  >
                    Download
                  </Button>
                  <Spacer />
                  {user.type !== 'Cliente' && (
                    <SinisterAddDocumentForm sinisterId={id} />
                  )}
                </Flex>
                {!!documents && documents?.length && !isLoadingDocuments && (
                  <Table type="secondary">
                    <Thead>
                      <Tr>
                        <Th>
                          <Checkbox
                            isChecked={
                              checkedItems.length ===
                              documents.map(document => document.id_check)
                                .length
                            }
                            isIndeterminate={
                              checkedItems.length > 0 &&
                              checkedItems.length !==
                                documents.map(document => document.id_check)
                                  .length
                            }
                            colorScheme="green"
                            onChange={() => {
                              const docsIds = documents.map(
                                document => document.id_check,
                              );
                              if (checkedItems.length === docsIds.length) {
                                setCheckedItems([]);
                              } else {
                                setCheckedItems(docsIds);
                              }
                            }}
                          >
                            <Text fontSize="13px">Nome do documento</Text>
                          </Checkbox>
                        </Th>
                        <Th>Data</Th>
                        <Th>Tipo</Th>
                        <Th textAlign="center">Download</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {documents.map((doc: SinisterDocuments) => (
                        <Tr key={doc.id_check}>
                          <Td>
                            <Stack pl={6} mt={1} spacing={1}>
                              <Checkbox
                                colorScheme="green"
                                isChecked={checkedItems.includes(doc.id_check)}
                                onChange={event => {
                                  event.stopPropagation();
                                  const index = checkedItems.indexOf(
                                    doc.id_check,
                                  );

                                  if (index > -1) {
                                    setCheckedItems([
                                      ...checkedItems.slice(0, index),
                                      ...checkedItems.slice(index + 1),
                                    ]);
                                  } else {
                                    setCheckedItems([
                                      ...checkedItems,
                                      doc.id_check,
                                    ]);
                                  }
                                }}
                              >
                                <Text fontSize="13px">{doc.nome}</Text>
                              </Checkbox>
                            </Stack>
                          </Td>
                          <Td>{doc.data_upload ? doc.data_upload : '-'}</Td>
                          <Td>{doc.tipo ? doc.tipo : '-'}</Td>
                          <Td>
                            <Text
                              color="gray.500"
                              fontSize="14px"
                              textAlign="center"
                            >
                              <IconButton
                                colorScheme="gray"
                                aria-label="Download"
                                icon={<Icon as={FiDownload} />}
                                onClick={() => handleDownloadDocument(doc)}
                                isDisabled={isDownloading}
                              />
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Flex>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>
      <RecentActivity sinisterId={data!.id} />
    </Box>
  );
};
